import { useState, useCallback } from 'react';

interface ValidationRules {
  required?: boolean;
  email?: boolean;
  minLength?: number;
  maxLength?: number;
  password?: boolean;
  confirmPassword?: string;
}

interface FieldState {
  value: string;
  error: string;
  isValid: boolean;
  touched: boolean;
}

export const useFormValidation = () => {
  const [fields, setFields] = useState<Record<string, FieldState>>({});

  const validateField = useCallback(
    (name: string, value: string, rules: ValidationRules) => {
      let error = '';

      if (rules.required && !value.trim()) {
        error = `${name} is required`;
      } else if (
        rules.email &&
        value &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
      ) {
        error = 'Please enter a valid email address';
      } else if (rules.password && value) {
        if (value.length < 8 || value.length > 20) {
          error = 'Password must be 8-20 characters';
        } else if (!/(?=.*[0-9])/.test(value)) {
          error = 'Password must contain at least 1 number';
        } else if (!/(?=.*[a-zA-Z])/.test(value)) {
          error = 'Password must contain at least 1 letter';
        } else if (!/(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(value)) {
          error = 'Password must contain at least 1 symbol';
        }
      } else if (rules.minLength && value && value.length < rules.minLength) {
        error = `${name} must be at least ${rules.minLength} characters`;
      } else if (rules.maxLength && value && value.length > rules.maxLength) {
        error = `${name} must be no more than ${rules.maxLength} characters`;
      } else if (
        rules.confirmPassword &&
        value &&
        value !== rules.confirmPassword
      ) {
        error = 'Passwords do not match';
      }

      const isValid = !error;

      setFields(prev => ({
        ...prev,
        [name]: {
          value,
          error,
          isValid,
          touched: true,
        },
      }));

      return isValid;
    },
    [],
  );

  const getFieldProps = useCallback(
    (name: string, rules: ValidationRules = {}) => ({
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setFields(prev => ({
          ...prev,
          [name]: {
            ...prev[name],
            value,
            error: '',
            touched: false,
          },
        }));
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
        validateField(name, e.target.value, rules);
      },
      className: fields[name]?.touched
        ? fields[name]?.isValid
          ? 'success'
          : 'error'
        : '',
      value: fields[name]?.value || '',
    }),
    [fields, validateField],
  );

  const getFieldError = useCallback(
    (name: string) => {
      return fields[name]?.touched ? fields[name]?.error : '';
    },
    [fields],
  );

  const isFormValid = useCallback(() => {
    return Object.values(fields).every(field => field.isValid);
  }, [fields]);

  return {
    getFieldProps,
    getFieldError,
    isFormValid,
    validateField,
  };
};
